import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import GoogleAuthButton, { useGoogleClientId } from '../common/GoogleAuthButton';
import FloatingShapes from '../common/FloatingShapes';
import SignUpForm from './SignUpForm';
import OTPForm from './OTPForm';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/auth-shell.css';
import {
  attemptLogin,
  attemptGoogleLogin,
  setTenantSession,
  needsCheckoutResume,
  getTenantId,
  isCurrentTenantOnHold,
  setCheckoutTenantId,
  registerOrganization,
  activateSubscriptionRemote,
  clearTenantSession,
} from '../../lib/tenant';
import { hydrateTenantWorkspace } from '../../lib/cloudSync';
import { getCurrentUser } from '../../lib/permissions';
import { EdgeFinancesLogo } from '../common/EdgeFinancesLogo';
import { loginCustomer } from '../../lib/customerPortal';
import { getAuthToken } from '../../api';
import { useTranslation } from '../../lib/i18n';
import { isIosLoginOnlyApp } from '../../lib/platform';

const Login = () => {
  const { t } = useTranslation();
  const iosLoginOnly = isIosLoginOnlyApp();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const notify = (message) => toast(message);
  const googleClientId = useGoogleClientId();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleError = params.get('google_error');
    if (googleError) {
      notify(
        googleError === 'missing_token'
          ? 'Google sign-in did not return a token. Try again.'
          : `Google sign-in failed: ${googleError}`
      );
      navigate('/login', { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Attempt a borrower (customer) sign-in; routes to the read-only portal on success. */
  const tryCustomerLogin = async (email, password) => {
    const res = await loginCustomer(email, password);
    if (res.ok) {
      notify('Signed in to your customer portal.');
      navigate('/customer');
      return true;
    }
    if (res.error && res.error !== 'Invalid email or password.') {
      notify(res.error);
      return true; // surfaced server/connection error — stop generic admin message
    }
    return false;
  };

  const enterSuperAdmin = (email) => {
    clearTenantSession();
    localStorage.setItem('userId', 'super-admin');
    localStorage.setItem('userEmail', email.trim().toLowerCase());
    localStorage.setItem('role', 'SUPER_ADMIN');
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('token', 'super-frontend-token');
    notify('Signed in as platform operator — managing all organizations.');
    navigate('/super-admin');
  };

  /** Establish a tenant-admin session and route to the right next screen. */
  const enterAdmin = async (org) => {
    setTenantSession(org);
    localStorage.setItem('userId', org.id);
    localStorage.setItem('userEmail', org.adminEmail);
    localStorage.setItem('role', 'ADMIN');
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('token', `saas_${org.id}`);
    if (org.accountHold) {
      notify('This organization is on hold. Contact billing to restore access.');
      navigate('/account-held');
      return;
    }
    if (org.subscriptionStatus !== 'active') {
      if (iosLoginOnly) {
        notify(
          'This workspace is not active. Manage subscription on the Edge Finance Pro website or contact your administrator.'
        );
        clearTenantSession();
        return;
      }
      setCheckoutTenantId(org.id);
      notify('Complete your subscription to open the dashboard.');
      navigate(
        `/checkout?tenant=${encodeURIComponent(org.id)}&plan=${encodeURIComponent(
          org.planId || 'premium'
        )}`
      );
      return;
    }
    // Pull this tenant's data from MongoDB into the local cache before entering.
    await hydrateTenantWorkspace();
    notify('Signed in.');
    navigate('/dashboard');
  };

  const LoginFunction = async (email, password) => {
    try {
      if (!email?.trim() || !password) {
        notify('Enter email and password.');
        return;
      }

      // 1) Primary path: authenticate against the backend (MongoDB).
      const outcome = await attemptLogin(email, password);

      if (outcome.status === 'super') {
        enterSuperAdmin(email);
        return;
      }

      if (outcome.status === 'admin') {
        await enterAdmin(outcome.org);
        return;
      }

      // 2) Backend reachable but not an admin/super login — maybe a borrower (customer).
      if (outcome.status === 'invalid') {
        if (await tryCustomerLogin(email, password)) return;
        notify(
          'Invalid email or password. Customers: use the login email from your lender and your 10-digit mobile as the password.'
        );
        return;
      }

      // 3) Backend unreachable. No offline mode — everything is database-backed.
      notify('Cannot reach the server. Please check your connection and try again.');
    } catch (error) {
      console.error('Error logging in:', error);
      notify('Could not sign in. Try again.');
    }
  };

  /** Google Sign-In handler (login). Routes new Google users to registration. */
  const handleGoogleLogin = async (credential) => {
    try {
      const outcome = await attemptGoogleLogin(credential);
      if (outcome.status === 'super') {
        enterSuperAdmin(getCurrentUser()?.email || '');
        return;
      }
      if (outcome.status === 'admin') {
        await enterAdmin(outcome.org);
        return;
      }
      if (outcome.status === 'noaccount') {
        notify('No workspace exists for this Google account yet. Register your organization first.');
        if (!iosLoginOnly) navigate('/register');
        return;
      }
      if (outcome.status === 'invalid') {
        notify(outcome.message || 'Could not sign in with Google. Please try again.');
        return;
      }
      notify('Cannot reach the server. Please check your connection and try again.');
    } catch (error) {
      console.error('Google sign-in error:', error);
      notify('Google sign-in failed. Try again.');
    }
  };

  const verifyOTP = async (_otp) => {
    setShowOTP(false);
    notify('This build runs without a mail server — use Sign in with any email + password instead.');
  };

  const SignUpFunction = async (email, password, name) => {
    if (iosLoginOnly) {
      notify('Create your organization on the Edge Finance Pro website, then sign in here.');
      return;
    }
    try {
      const org = await registerOrganization({
        name: (name && name.trim()) || email.split('@')[0] || 'My company',
        adminEmail: email,
        password,
        planId: 'workspace',
      });
      setTenantSession({
        id: org.id,
        name: org.name,
        slug: org.slug,
        planId: 'workspace',
        subscriptionStatus: 'active',
      });
      await activateSubscriptionRemote(org.id, 'workspace');
      localStorage.setItem('userId', org.id);
      localStorage.setItem('userEmail', org.adminEmail);
      localStorage.setItem('role', 'ADMIN');
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('token', `saas_${org.id}`);
      await hydrateTenantWorkspace();
      notify('Organization created. You can sign in with the same email and password.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error signing up:', error);
      notify(error?.message || 'Could not create account (email may already exist).');
    }
  };

  useEffect(() => {
    if (location.state?.checkoutOk) {
      toast.success('Subscription is active. Sign in with your organization admin email.');
      navigate('/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = localStorage.getItem('token');
    if (isLoggedIn && token && getAuthToken()) {
      if (localStorage.getItem('role') === 'SUPER_ADMIN' && token.startsWith('super-frontend-token') && getAuthToken()) {
        navigate('/super-admin');
        return;
      }
      if (isCurrentTenantOnHold()) {
        navigate('/account-held');
        return;
      }
      if (needsCheckoutResume()) {
        if (iosLoginOnly) {
          clearTenantSession();
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('role');
          return;
        }
        const tid = getTenantId();
        navigate(tid ? `/checkout?tenant=${encodeURIComponent(tid)}` : '/checkout');
      } else {
        navigate('/dashboard');
      }
    }
  }, [navigate, iosLoginOnly]);

  const heading = showOTP ? t('login.enterOtp') : isSignUp && !iosLoginOnly ? t('login.createAccount') : t('login.welcome');
  const subtitle = showOTP
    ? t('login.otpSubtitle')
    : isSignUp && !iosLoginOnly
      ? t('login.signUpSubtitle')
      : t('login.subtitle');

  return (
    <div className="auth-page font-sans">
      <FloatingShapes />
      <ToastContainer />
      <div className="auth-card">
        <div className="auth-card-body">
          <div className="auth-logo">
            <EdgeFinancesLogo variant="onLight" size="lg" showWordmark className="justify-center" />
          </div>

          <h1 className="auth-title font-display">{heading}</h1>
          <p className="auth-subtitle">{subtitle}</p>

          <div className="auth-form">
            {showOTP ? (
              <OTPForm verifyOTP={verifyOTP} />
            ) : isSignUp && !iosLoginOnly ? (
              <SignUpForm SignUpFunction={SignUpFunction} />
            ) : (
              <LoginForm LoginFunction={LoginFunction} />
            )}
          </div>

          {!showOTP && !iosLoginOnly && googleClientId ? (
            <>
              <div className="auth-divider">
                <span>{t('login.or')}</span>
              </div>
              <GoogleAuthButton
                text={isSignUp ? 'signup_with' : 'signin_with'}
                onCredential={handleGoogleLogin}
                onError={(err) => notify(err?.message || 'Google sign-in failed.')}
              />
            </>
          ) : null}

          {!iosLoginOnly ? (
            <p className="auth-switch">
              {isSignUp ? t('login.alreadyHaveAccount') : t('login.newOrganization')}{' '}
              {isSignUp ? (
                <button
                  type="button"
                  className="auth-link"
                  onClick={() => {
                    setIsSignUp(false);
                    setShowOTP(false);
                  }}
                >
                  {t('login.signIn')}
                </button>
              ) : (
                <NavLink to="/register" className="auth-link">
                  {t('login.register')}
                </NavLink>
              )}
            </p>
          ) : null}

        </div>
      </div>
    </div>
  );
};

export default Login;
